import { CreateDerivationMapper } from "@catalogue/data/mappers/derivation/create-derivation.mapper";
import { DerivationMapper } from "@catalogue/data/mappers/derivation/derivation.mapper";
import { ServiceMapper } from "@catalogue/data/mappers/service/service.mapper";
import { CatalogueDerivationError } from "@catalogue/domain/errors/catalogue-derivation.error";
import { CatalogueServiceError } from "@catalogue/domain/errors/catalogue-service.error";
import { DerivationMapperError } from "@catalogue/domain/errors/derivation/derivation-mapper.error";
import { ServiceMapperError } from "@catalogue/domain/errors/service/service-mapper.error";
import {
    CreateDerivation,
    Derivation,
} from "@catalogue/domain/models/derivation.model";
import { Service } from "@catalogue/domain/models/service.model";
import { UpdateDerivationData } from "@catalogue/domain/usecases/update-derivation.usecase";
import { coreTypes } from "@core/core-types.di";
import { EXPAND_ALL, FieldsQuery } from "@core/data/dto/fields.dto";
import { type Http } from "@core/data/infrastructures/http/http";
import { OrderMapper } from "@core/data/mappers/order.mapper";
import { Order } from "@core/domain/models/order.model";
import { Pagination } from "@core/domain/models/pagination";
import { Either } from "@core/domain/types/either";
import { ServicesDto } from "@entity/data/dto/services.dto";
import { plainToClass } from "class-transformer";
import { inject, injectable } from "inversify";
import { DerivationDto, DerivationsDto } from "../dto/derivation.dto";
import {
    ServiceDto,
    ServiceSearchFilters,
    ServicesQuery,
    servicesOrderMap,
} from "../dto/service.dto";
import { UpdateDerivationMapper } from "../mappers/derivation/edit-derivation.mapper";

const SERVICES_PATH = "/services/";
const DERIVATIONS_PATH = "/derivations/";

// Interfaz para los filtros que recibe este método (debe incluir assigned_by si el VM lo pasa)
interface DerivationSearchFilters {
    beneficiaryId?: number;
    serviceId?: number;
    entityId?: number;
    assigned_by?: number;
    ordering?: string;
}

// Interfaz para los parámetros de consulta que se envían a la API
interface DerivationsQuery {
    limit?: number;
    offset?: number;
    beneficiaryId?: number; // <-- Nombres que espera la API
    serviceId?: number;
    entityId?: number;
    assigned_by?: number;
    ordering?: string;
}

@injectable()
export class CatalogueDatasource {
    constructor(
        @inject(coreTypes.infrastructure.Http)
        private readonly http: Http,
        @inject(OrderMapper)
        private readonly orderMapper: OrderMapper,
        @inject(ServiceMapper)
        private readonly serviceMapper: ServiceMapper,
        @inject(CreateDerivationMapper)
        private readonly createDerivationMapper: CreateDerivationMapper,
        @inject(DerivationMapper)
        private readonly derivationMapper: DerivationMapper,
        @inject(UpdateDerivationMapper)
        private readonly updateDerivationMapper: UpdateDerivationMapper,
    ) {}

    async fetchServices(
        pagination?: Pagination,
        filters?: ServiceSearchFilters,
        order?: Order<Service>,
    ): Promise<Either<CatalogueServiceError, Service[]>> {
        const query: ServicesQuery = {
            ...(pagination && {
                limit: pagination.pageSize,
                offset: pagination.offset,
            }),
            ...(filters?.name && { name: filters.name }),
            ...(filters?.entityId && { entity: filters.entityId }),
        };

        if (order) {
            const orderQuery = this.orderMapper.mapToDto<Service, ServiceDto>(
                order.field,
                order.direction,
                servicesOrderMap,
            );
            if (orderQuery) query.ordering = orderQuery;
        }

        // Change the expected type to ServicesDto instead of ServiceDto[]
        const servicesResult = await this.http.get<ServicesDto>(SERVICES_PATH, {
            query,
        });

        // Access the results array within the response data
        return servicesResult
            .mapLeft(() => new CatalogueServiceError("fetchServicesError"))
            .map((response) =>
                (response.data.results || []) // Use results property and provide fallback
                    .map((serviceDto) =>
                        this.serviceMapper.map(
                            plainToClass(ServiceDto, serviceDto),
                        ),
                    )
                    .filter(
                        (service): service is Service => service !== undefined,
                    ),
            );
    }

    async fetchServiceById(
        serviceId: number,
    ): Promise<Either<CatalogueServiceError | ServiceMapperError, Service>> {
        const query: FieldsQuery = {
            expand: EXPAND_ALL,
        };

        const serviceResult = await this.http.get<ServiceDto>(
            `${SERVICES_PATH}${serviceId}/`,
            { query },
        );

        return serviceResult
            .mapLeft(() => new CatalogueServiceError("fetchServiceByIdError"))
            .flatMap((response) => {
                const service = this.serviceMapper.map(
                    plainToClass(ServiceDto, response.data),
                );

                if (!service) return Either.Left(new ServiceMapperError());

                return Either.Right(service);
            });
    }

    async createService(
        newService: Omit<Service, "id">,
    ): Promise<Either<CatalogueServiceError | ServiceMapperError, Service>> {
        const createServiceBody =
            this.serviceMapper.mapToCreateBody(newService);

        console.log("createServiceBody!!!", createServiceBody);

        const query: FieldsQuery = {
            expand: EXPAND_ALL,
        };

        const createServiceResult = await this.http.post<ServiceDto>(
            SERVICES_PATH,
            createServiceBody,
            { query },
        );

        return createServiceResult
            .mapLeft(() => new CatalogueServiceError("createServiceError"))
            .flatMap((response) => {
                const createdService = this.serviceMapper.map(
                    plainToClass(ServiceDto, response.data),
                );

                if (!createdService)
                    return Either.Left(new ServiceMapperError());

                return Either.Right(createdService);
            });
    }

    async deleteService(
        serviceId: number,
    ): Promise<Either<CatalogueServiceError, boolean>> {
        const deleteServiceResult = await this.http.delete(
            `${SERVICES_PATH}${serviceId}/`,
        );

        return deleteServiceResult
            .mapLeft(() => new CatalogueServiceError("deleteServiceError"))
            .map(() => true);
    }

    //DERIVATION
    async fetchDerivations(
        pagination?: Pagination,
        filters?: DerivationSearchFilters,
    ): Promise<
        Either<
            CatalogueDerivationError | DerivationMapperError,
            { derivations: Derivation[]; count: number }
        >
    > {
        const query: DerivationsQuery = {};

        // Paginación
        if (pagination) {
            query.limit = pagination.pageSize;
            query.offset = pagination.offset;
        }

        // --- Añadir lógica para construir query params desde filters ---
        if (filters?.beneficiaryId !== undefined) {
            query.beneficiaryId = filters.beneficiaryId;
        }
        if (filters?.serviceId !== undefined) {
            query.serviceId = filters.serviceId;
        }
        if (filters?.entityId !== undefined) {
            query.entityId = filters.entityId;
        }
        // --- ¡AÑADIR EL NUEVO FILTRO! ---
        if (filters?.assigned_by !== undefined) {
            query.assigned_by = filters.assigned_by; // Usar la clave que la API espera como query param
        }
        if (filters?.ordering !== undefined) {
            query.ordering = filters.ordering;
        }

        const result = await this.http.get<DerivationsDto>(DERIVATIONS_PATH, {
            query: query as Record<string, unknown>,
        });

        return result
            .mapLeft(
                () => new CatalogueDerivationError("fetchDerivationsError"),
            )
            .flatMap((response) => {
                const dtos = response.data.results ?? [];
                const count = response.data.count ?? 0;
                const derivations = dtos.mapNotNull((dto) =>
                    this.derivationMapper.map(dto),
                );
                return Either.Right({ derivations, count });
            });
    }

    async createDerivation(
        newDerivation: CreateDerivation,
    ): Promise<
        Either<CatalogueDerivationError | DerivationMapperError, Derivation>
    > {
        const createDerivationBody =
            this.createDerivationMapper.map(newDerivation);

        const query: FieldsQuery = {
            expand: EXPAND_ALL,
        };

        const createDerivationResult = await this.http.post<DerivationDto>(
            DERIVATIONS_PATH,
            createDerivationBody,
            { query },
        );

        return createDerivationResult
            .mapLeft(
                () => new CatalogueDerivationError("createDerivationError"),
            )
            .flatMap((response) => {
                const createdDerivation = this.derivationMapper.map(
                    response.data,
                );

                if (!createdDerivation)
                    return Either.Left(new DerivationMapperError());

                return Either.Right(createdDerivation);
            });
    }

    async fetchDerivationById(
        derivationId: number,
    ): Promise<
        Either<CatalogueDerivationError | DerivationMapperError, Derivation>
    > {
        const query: FieldsQuery = {
            expand: EXPAND_ALL,
        };

        const result = await this.http.get<DerivationDto>(
            `${DERIVATIONS_PATH}${derivationId}/`,
            { query },
        );

        return result
            .mapLeft(
                () => new CatalogueDerivationError("fetchDerivationByIdError"),
            )
            .flatMap((response) => {
                const derivation = this.derivationMapper.map(response.data);

                if (!derivation) {
                    return Either.Left(new DerivationMapperError());
                }

                return Either.Right(derivation);
            });
    }

    async updateDerivation(
        derivation: UpdateDerivationData,
    ): Promise<
        Either<CatalogueDerivationError | DerivationMapperError, Derivation>
    > {
        const updateDerivationBody =
            this.updateDerivationMapper.map(derivation);

        const query: FieldsQuery = {
            expand: EXPAND_ALL,
        };

        const updateDerivationResult = await this.http.patch<DerivationDto>(
            `${DERIVATIONS_PATH}${derivation.id}/`,
            updateDerivationBody,
            { query },
        );

        return updateDerivationResult
            .mapLeft(
                () => new CatalogueDerivationError("updateDerivationError"),
            )
            .flatMap((response) => {
                const updatedDerivation = this.derivationMapper.map(
                    response.data,
                );

                if (!updatedDerivation)
                    return Either.Left(new DerivationMapperError());

                return Either.Right(updatedDerivation);
            });
    }
}
